import React from "react";
import PropTypes from "prop-types";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import useApi from "../hooks/useApi";

/**
 * ApplicationURIDeleteModal - A modal component for deleting an application's URI.
 *
 * @param {object} props - The component props.
 * @param {object} props.config - Configuration object containing URI details.
 * @param {boolean} props.isOpen - Boolean indicating if the modal is open.
 * @param {Function} props.onChangeBulk - Function to handle bulk updates.
 * @param {string|number} props.applicationId - The ID of the application.
 * @param {string} props.applicationURIName - The name of the application URI.
 * @param {string} props.environmentName - The name of the environment.
 * @returns {JSX.Element} The rendered ApplicationURIDeleteModal component.
 */
const ApplicationURIDeleteModal = (props) => {
	const {
		config,
		isOpen,
		onChangeBulk,
		applicationId,
		applicationURIName,
		environmentName
	} = props;

	const api = useApi();

	/**
	 * handleSubmit - Handles the deletion of the application URI.
	 *
	 * @returns {void}
	 */
	const handleSubmit = async () => {
		const application = await api.baseApiCall({
			endpoint: `/application/${applicationId}/environment/${environmentName}/uri/${applicationURIName}`,
			options: {
				method: "DELETE"
			}
		});
		onChangeBulk({
			applicationURIDeleteModalIsOpen: false,
			application: application,
			environments: application.environments
		});
	};

	return (
		<Dialog
			open={isOpen}
			onClose={() => onChangeBulk({ applicationURIDeleteModalIsOpen: false })}
			aria-labelledby="delete-uri-dialog-title"
			aria-describedby="delete-uri-dialog-description"
		>
			<DialogTitle id="delete-uri-dialog-title">
				{`Delete "${applicationURIName} API" Application URI?`}
			</DialogTitle>
			<DialogContent>
				<Typography id="delete-uri-dialog-description">
					"WARNING: Deleting an application URI will prevent that application URI from appearing in the config response from the API and no longer be available for use in code. Are you sure?"
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => onChangeBulk({ applicationURIDeleteModalIsOpen: false })}>
					"Cancel"
				</Button>
				<Button variant="contained" color="error" onClick={handleSubmit}>
					"DELETE"
				</Button>
			</DialogActions>
		</Dialog>
	);
};

ApplicationURIDeleteModal.propTypes = {
	config: PropTypes.shape({
		uri: PropTypes.shape({
			base: PropTypes.string.isRequired
		}).isRequired
	}).isRequired,
	isOpen: PropTypes.bool.isRequired,
	onChangeBulk: PropTypes.func.isRequired,
	applicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	applicationURIName: PropTypes.string.isRequired,
	environmentName: PropTypes.string.isRequired
};

export default ApplicationURIDeleteModal;

